import React, { useState, useRef, useCallback, useEffect } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Professional chat input with auto-resize
 * Styled like Intercom/modern chat widgets
 */
export function ChatInput({
  onSend,
  disabled = false,
  placeholder = "Type a message...",
  className,
}: ChatInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const maxHeight = 120;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [message, adjustHeight]);

  // Scroll input into view when focused (mobile keyboard)
  const handleFocus = useCallback(() => {
    // Short delay to let the keyboard open before scrolling
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 300);
  }, []);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmed = message.trim();
    if (!trimmed || disabled) return;

    onSend(trimmed);
    setMessage("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={cn("p-4 pb-safe bg-background", className)}
    >
      <div
        className={cn(
          "flex items-end gap-2 rounded-2xl border bg-background p-2 transition-shadow",
          "focus-within:ring-2 focus-within:ring-offset-2",
          "focus-within:ring-[var(--widget-primary)]",
        )}
      >
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            "flex-1 resize-none bg-transparent border-0 text-sm leading-relaxed",
            "placeholder:text-muted-foreground/60",
            "focus:outline-none focus:ring-0",
            "min-h-[24px] max-h-[120px] py-1.5 px-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            // Prevent iOS zoom on focus
            "text-[16px] sm:text-sm",
          )}
        />
        <Button
          type="submit"
          size="icon"
          disabled={disabled || !message.trim()}
          className={cn(
            "h-9 w-9 rounded-full shrink-0",
            "transition-all duration-200",
            "disabled:opacity-40",
            message.trim() && "scale-100",
            !message.trim() && "scale-95",
          )}
          style={{
            backgroundColor: message.trim()
              ? "var(--widget-primary)"
              : undefined,
          }}
        >
          <Send className="h-4 w-4" />
          <span className="sr-only">Send</span>
        </Button>
      </div>
    </form>
  );
}
